import React, { useEffect, useState } from 'react';
import { Series } from '@cambridgeassessment/lib-ee-commons';
import { Button, Dropdown, Subheader, TextField, Title } from '@cambridgeassessment/cambridge-ui';
import { SelectTitleWrapper, StyledSelectsRow } from '../ManageSampleChanges/ManageSampleChanges.styles';
import { StyledInputContainer } from '../Content.styles';
import { ButtonWrapper, StyledTitle } from './SubmissionFailures.styles';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import * as seriesThunks from '../../../store/series/thunks';
import Loader from '../../../components/Loader/Loader';
import { SearchValues } from '../../../types/RmaErrors';

const pastTwoYears = new Date();
pastTwoYears.setFullYear(pastTwoYears.getFullYear() - 2);

type Props = {
  onChangeSearchValues: (searhValues: Partial<SearchValues>) => void;
  inputValues: SearchValues;
  onSearch: VoidFunction;
  searchDisabled: boolean;
};

const SearchFields: React.FC<Props> = ({
  onChangeSearchValues,
  onSearch,
  inputValues,
  searchDisabled,
}): JSX.Element => {
  const dispatch = useDispatch();
  const [errorMsg, setErrorMsg] = useState('');

  const {
    userData: { businessUnit },
    series: { seriesData, isLoading },
  } = useSelector((state: RootState) => state);

  const twoYearsSeries = seriesData?.filter((series) => new Date(series.endDate) > pastTwoYears) || [];

  useEffect(() => {
    if (!businessUnit) return;

    dispatch(seriesThunks.getSeriesData(businessUnit.id));
  }, [businessUnit, dispatch]);

  const renderSeries = (renderedSeriesId: Series['id']): string =>
    seriesData?.find((series) => series.id === renderedSeriesId)?.name ?? '';

  const onSeriesChange = (changedSeriesId: Series['id']): void => {
    onChangeSearchValues({ seriesId: changedSeriesId.toString() });
  };

  const onSyllabusChange = (value: string) => {
    onChangeSearchValues({ assessmentCode: value });
  };

  const onComponentChange = (value: string) => {
    onChangeSearchValues({ componentNumber: value });
  };

  const onLearnerChange = (value: string) => {
    const regexp = /^\d*$/;
    const matched = regexp.exec(value);

    if (errorMsg) {
      setErrorMsg('');
    }
    if (!matched) {
      setErrorMsg('Candidate number must contain numbers only');
    }
    onChangeSearchValues({ learnerNumber: value });
  };

  const onCentreChange = (value: string) => {
    onChangeSearchValues({ centreId: value });
  };

  const onErrorChange = (value: string) => {
    onChangeSearchValues({ errorType: value });
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <StyledInputContainer>
        <Title id='rm-series'>Select series</Title>
        <Dropdown<Series['id']>
          data={twoYearsSeries.map((twoYears) => twoYears.id)}
          renderOption={renderSeries}
          renderSelectedOption={renderSeries}
          dataQaId='series-select'
          onChange={onSeriesChange}
          value={Number(inputValues.seriesId)}
          placeholder='Please select'
          title='Select series'
        />
      </StyledInputContainer>
      <StyledTitle data-qa-id='rm-component-search-title'>Search for components</StyledTitle>
      <StyledSelectsRow>
        <StyledInputContainer>
          <SelectTitleWrapper>
            <Subheader>Syllabus</Subheader>
          </SelectTitleWrapper>
          <TextField
            dataQaId='search-syllabus'
            onChange={onSyllabusChange}
            placeholder='Search by syllabus number'
            value={inputValues.assessmentCode}
            helperText='Optional'
            onEnter={onSearch}
          />
        </StyledInputContainer>
        <StyledInputContainer>
          <SelectTitleWrapper>
            <Subheader>Component</Subheader>
          </SelectTitleWrapper>
          <TextField
            dataQaId='search-component'
            onChange={onComponentChange}
            placeholder='Search by component number'
            value={inputValues.componentNumber}
            helperText='Optional'
            onEnter={onSearch}
          />
        </StyledInputContainer>
      </StyledSelectsRow>
      <StyledTitle data-qa-id='rm-candidates-search-title'>Search for one or more candidates</StyledTitle>
      <StyledSelectsRow>
        <StyledInputContainer>
          <SelectTitleWrapper>
            <Subheader>Centre number</Subheader>
          </SelectTitleWrapper>
          <TextField
            dataQaId='search-centre'
            onChange={onCentreChange}
            placeholder='Search by centre number'
            value={inputValues.centreId}
            helperText='Optional'
            onEnter={onSearch}
          />
        </StyledInputContainer>
        <StyledInputContainer>
          <SelectTitleWrapper>
            <Subheader>Candidate number</Subheader>
          </SelectTitleWrapper>
          <TextField
            dataQaId='search-candidate'
            onChange={onLearnerChange}
            placeholder='Search by candidate number'
            value={inputValues.learnerNumber}
            helperText='Optional'
            errorMsg={errorMsg}
            onEnter={onSearch}
          />
        </StyledInputContainer>
      </StyledSelectsRow>
      <StyledTitle data-qa-id='rm-error-search-title'>Search for errors</StyledTitle>
      <StyledInputContainer>
        <SelectTitleWrapper>
          <Subheader>Type of error</Subheader>
        </SelectTitleWrapper>
        <TextField
          dataQaId='search-error'
          onChange={onErrorChange}
          placeholder='Search by error type'
          value={inputValues.errorType}
          helperText='Optional'
          onEnter={onSearch}
        />
      </StyledInputContainer>
      <ButtonWrapper>
        <Button qaId='search-button' onClick={onSearch} disabled={searchDisabled}>
          Search
        </Button>
      </ButtonWrapper>
    </>
  );
};

export default SearchFields;
